import {
  AccessTime,
  Checkroom,
  Cancel,
  ChatBubble,
  CheckCircle,
  CrisisAlert,
  EventBusyOutlined,
  Groups,
  ListAlt,
  LocalAtm,
  SearchOffOutlined,
  StoreMallDirectory,
  TrendingUp,
  Warning,
} from "@mui/icons-material";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  type TableCellProps,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import AnalyticsCard from "@/components/common/AnalyticsCard";
import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import CraveScoreCard from "@/components/common/CraveScoreCard/CraveScoreCard";
import { DataFreshnessFooter } from "@/components/common/DataFreshnessFooter";
import LockedAnalyticsCard from "@/components/common/LockedAnalyticsCard";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import {
  ComparisonPageHeaderShell,
  InsightsHeaderSelectorField,
  InsightsHeaderSelectorRow,
} from "@/components/common/ReportingPageHeaderShell";
import ReportingWindowSelector from "@/components/common/ReportingWindowSelector";
import StoreMultiSelect from "@/components/common/StoreMultiSelect";
import { TableSkeletonRows } from "@/components/common/TableSkeletonRows";
import { tableInCardSx } from "@/components/common/unifiedTableSx";
import PlaceholderImage from "@/components/common/PlaceholderImage/PlaceholderImage";
import AreaFilter from "@/components/filters/AreaFilter";
import { useAuth } from "@/contexts/AuthContext";
import { useCanceledRequests } from "@/hooks/useCanceledRequests";
import { useCantFindRequests } from "@/hooks/useCantFindRequests";
import { useConversionRate } from "@/hooks/useConversionRate";
import { useCustomerSatisfaction } from "@/hooks/useCustomerSatisfaction";
import { useDateRangeBadge } from "@/hooks/useDateRangeBadge";
import { useDwellTime } from "@/hooks/useDwellTime";
import i18n from "@/i18n/i18n";
import { useFulfilledRequests } from "@/hooks/useFulfilledRequests";
import { useMissedRequests } from "@/hooks/useMissedRequests";
import { useNamedShoppers } from "@/hooks/useNamedShoppers";
import { usePopularTriedOnItems } from "@/hooks/usePopularTriedOnItems";
import { useResponseTime } from "@/hooks/useResponseTime";
import { useStoreAreas } from "@/hooks/useStoreAreas";
import { useStoreRevenue } from "@/hooks/useStoreRevenue";
import {
  storePerformanceTrendMetrics,
  type StorePerformanceMetric,
  useStorePerformanceTrends,
} from "@/hooks/useStorePerformanceTrends";
import { useTotalRequests } from "@/hooks/useTotalRequests";
import { useTriedOnValue } from "@/hooks/useTriedOnValue";
import { useUnitsPerSession } from "@/hooks/useUnitsPerSession";
import { StoreInsightsPanel } from "@/pages/reports/shared/StoreInsightsPanel";
import { STORE_INSIGHTS_SINGLE_STORE_SELECTION_SESSION_KEY } from "@/pages/store-insights/shared/storeInsightsSelectionSession";
import { useStoreInsightsDeviceTypeFilter } from "@/pages/store-insights/shared/useStoreInsightsDeviceTypeFilter";
import { useStoreInsightsSharedDateWindow } from "@/pages/store-insights/shared/useStoreInsightsSharedDateWindow";
import { useStoreInsightsSingleStoreSelection } from "@/pages/store-insights/shared/useStoreInsightsSingleStoreSelection";
import { shouldShowInsightsTrends } from "@/pages/store-insights/shared/insightsFeatureGates";
import { useStoreInsightsTrendComparison } from "@/pages/store-insights/shared/useStoreInsightsTrendComparison";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { Store } from "@/services/features/StoreService";
import { storePerformanceService } from "@/services/StorePerformanceService";
import { getCraveScoreConfigForDevice } from "@/utils/craveScore";
import { formatCurrencyInStore } from "@/utils/currency";
import { formatTrendChange } from "@/utils/analyticsTrend";
import { formatDecimal, formatInteger, formatPercentFromWhole } from "@/utils/number";
import { UserRole } from "@/utils/roleManager";
import { formatStoreSelectorLabel } from "@/utils/store";
import { formatStoreDateRange, getBrowserTimezone, parseStoreDateKey } from "@/utils/storeTime";
import { formatDurationShort } from "@/utils/time";

type PopularItemsSortField =
  "title" | "sku" | "size" | "triedOnTimes" | "numberSold" | "quantityInStock";
type SortDirection = "asc" | "desc";

interface PopularItemsSortableHeaderProps {
  field: PopularItemsSortField;
  label: string;
  sortField: PopularItemsSortField;
  sortDirection: SortDirection;
  onSort: (field: PopularItemsSortField) => void;
  nextSortDirectionLabel: string;
  align?: TableCellProps["align"];
}

const PopularItemsSortableHeader: React.FC<PopularItemsSortableHeaderProps> = ({
  field,
  label,
  sortField,
  sortDirection,
  onSort,
  nextSortDirectionLabel,
  align,
}) => {
  const isActive = field === sortField;

  return (
    <TableCell align={align} sortDirection={isActive ? sortDirection : false}>
      <TableSortLabel
        active={isActive}
        aria-label={nextSortDirectionLabel}
        direction={isActive ? sortDirection : "asc"}
        onClick={() => onSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
};

const StorePerformance: React.FC = () => {
  const { t, i18n: translationI18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [popularItemsSortField, setPopularItemsSortField] =
    useState<PopularItemsSortField>("triedOnTimes");
  const [popularItemsSortDirection, setPopularItemsSortDirection] = useState<SortDirection>("desc");
  const browserTimezone = getBrowserTimezone();
  const navigate = useNavigate();
  const { user } = useAuth();
  const showTrends = true;
  const { stores, selectedStore, loading, onStoreChange } = useStoreInsightsSingleStoreSelection({
    sessionKey: STORE_INSIGHTS_SINGLE_STORE_SELECTION_SESSION_KEY,
  });
  // Render numeric rating with smaller black stars out of 5, supporting tenths with partial fill
  const renderStarRating = (val: string): React.ReactNode => {
    if (!val || val === "--") return "--";
    const rating = parseFloat(val);
    if (isNaN(rating)) return val;
    const clamped = Math.max(0, Math.min(5, rating));
    const percent = (clamped / 5) * 100;
    return (
      <span
        className="inline-flex items-center gap-1"
        aria-label={t("storeInsights.storePerformance.ratingOutOfFive", {
          rating,
        })}
        title={t("storeInsights.storePerformance.ratingTooltip", { rating })}
      >
        <span>{val}</span>
        <span
          className="relative inline-block text-2xl leading-none align-middle"
          aria-hidden="true"
        >
          <span className="text-gray-300">★★★★★</span>
          <span
            className="absolute left-0 rtl:left-auto rtl:right-0 top-0 overflow-hidden text-black"
            style={{ width: `${percent}%` }}
          >
            ★★★★★
          </span>
        </span>
      </span>
    );
  };
  const shortcutDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastShortcutAtRef = useRef<number>(0);
  const [latestSalesDate, setLatestSalesDate] = useState<Date | null>(null);
  const [salesWindowStoreId, setSalesWindowStoreId] = useState<number | null>(null);
  const [, setLatestFootTrafficDate] = useState<Date | null>(null);
  const { period, customRange, currentDateRange, onPeriodChange, onCustomRangeChange } =
    useStoreInsightsSharedDateWindow({
      defaultPeriod: "yesterday",
    });
  const { deviceType, onDeviceTypeChange } = useStoreInsightsDeviceTypeFilter({
    selectionSession: {
      kind: "single",
      key: STORE_INSIGHTS_SINGLE_STORE_SELECTION_SESSION_KEY,
    },
    selectionReady: !loading && selectedStore !== null,
    preserveAllInUrl: true,
  });
  const { areas, loading: areasLoading } = useStoreAreas(selectedStore?.id ?? null);
  const [areaSelection, setAreaSelection] = useState<{
    storeId: number;
    areaIds: number[];
  } | null>(null);
  // An area belongs to one store, so switching stores clears the selection.
  const areaIds =
    areaSelection && areaSelection.storeId === selectedStore?.id
      ? areaSelection.areaIds
      : undefined;
  const selectedAreaNames = areas
    .filter((area) => areaIds?.includes(area.id))
    .map((area) => area.name);
  const supportsStoreRevenue = deviceType === "all" || deviceType === "FITTING_ROOM";
  const supportsConversionRate = deviceType === "all" || deviceType === "FITTING_ROOM";
  const supportsRequestInsights = deviceType !== "RFID_CENTRAL";
  const {
    comparisonWindow,
    setComparisonWindow,
    anchorComparisonToWeekdays,
    setAnchorComparisonToWeekdays,
  } = useStoreInsightsTrendComparison(period, currentDateRange);
  const { freshnessFooterIcon, freshnessFooterText, freshnessFooterTooltip } = useDateRangeBadge({
    period,
  });

  const triedOnValueData = useTriedOnValue(
    selectedStore ? [selectedStore] : null,
    currentDateRange,
    deviceType,
    areaIds,
  );
  const unitsPerSessionData = useUnitsPerSession(
    selectedStore ? [selectedStore] : null,
    currentDateRange,
    deviceType,
    areaIds,
  );
  const missedRequestsData = useMissedRequests(
    selectedStore ? [selectedStore] : null,
    currentDateRange,
    deviceType,
    undefined,
    areaIds,
  );
  const fulfilledRequestsData = useFulfilledRequests(
    selectedStore ? [selectedStore] : null,
    currentDateRange,
    deviceType,
    areaIds,
  );
  const cantFindRequestsData = useCantFindRequests(
    selectedStore ? [selectedStore] : null,
    currentDateRange,
    deviceType,
    undefined,
    areaIds,
  );
  const responseTimeData = useResponseTime(
    selectedStore ? [selectedStore.id] : null,
    currentDateRange,
    deviceType,
    areaIds,
  );
  const revenueCardData = useStoreRevenue(
    selectedStore?.id ?? null,
    currentDateRange,
    latestSalesDate,
    deviceType,
    selectedStore,
    supportsStoreRevenue && salesWindowStoreId === selectedStore?.id,
    areaIds,
  );
  const conversionCardData = useConversionRate(
    selectedStore?.id ?? null,
    currentDateRange,
    latestSalesDate,
    deviceType,
    supportsConversionRate && salesWindowStoreId === selectedStore?.id,
    areaIds,
  );
  const popularItemsData = usePopularTriedOnItems(
    period !== "today" ? (selectedStore?.id ?? null) : null,
    currentDateRange,
    areaIds,
  );
  const customerSatisfactionData = useCustomerSatisfaction(
    selectedStore ? [selectedStore.id] : null,
    currentDateRange,
  );
  const totalRequestsData = useTotalRequests(
    selectedStore ? [selectedStore] : null,
    currentDateRange,
    deviceType,
    areaIds,
  );
  const canceledRequestsData = useCanceledRequests(
    selectedStore ? [selectedStore] : null,
    currentDateRange,
    deviceType,
    areaIds,
  );
  const dwellTimeData = useDwellTime(
    selectedStore ? [selectedStore.id] : null,
    currentDateRange,
    deviceType,
    areaIds,
  );
  const namedShoppersData = useNamedShoppers(
    selectedStore ? [selectedStore.id] : null,
    currentDateRange,
    deviceType,
    areaIds,
  );
  const titleLoading = Boolean(
    loading || missedRequestsData.loading || cantFindRequestsData.percentage.loading,
  );
  const popularItemsLoading = loading || popularItemsData.loading;
  const popularItemsLoadingKey = useMemo(
    () =>
      [
        selectedStore?.id ?? "",
        currentDateRange.start.getTime(),
        currentDateRange.end.getTime(),
        browserTimezone,
      ].join("|"),
    [browserTimezone, currentDateRange.end, currentDateRange.start, selectedStore?.id],
  );
  const sortedPopularItems = useMemo(() => {
    const collator = new Intl.Collator(
      translationI18n.resolvedLanguage || translationI18n.language,
      {
        numeric: true,
        sensitivity: "base",
      },
    );

    return popularItemsData.items.toSorted((left, right) => {
      const comparison =
        popularItemsSortField === "title" ||
        popularItemsSortField === "sku" ||
        popularItemsSortField === "size"
          ? collator.compare(left[popularItemsSortField], right[popularItemsSortField])
          : left[popularItemsSortField] - right[popularItemsSortField];

      if (comparison !== 0) {
        return popularItemsSortDirection === "asc" ? comparison : -comparison;
      }

      return collator.compare(left.sku, right.sku);
    });
  }, [
    translationI18n.language,
    translationI18n.resolvedLanguage,
    popularItemsData.items,
    popularItemsSortDirection,
    popularItemsSortField,
  ]);
  const handlePopularItemsSort = useCallback(
    (field: PopularItemsSortField) => {
      setPopularItemsSortDirection((currentDirection) => {
        if (field === popularItemsSortField) {
          return currentDirection === "asc" ? "desc" : "asc";
        }

        return field === "title" || field === "sku" || field === "size" ? "asc" : "desc";
      });
      setPopularItemsSortField(field);
    },
    [popularItemsSortField],
  );
  const popularItemsNextSortLabel = (field: PopularItemsSortField) =>
    t("storeInsights.common.aria.toggleSortDirection", {
      direction: t(
        field === popularItemsSortField && popularItemsSortDirection === "asc"
          ? "storeInsights.common.sortValue.descending"
          : "storeInsights.common.sortValue.ascending",
      ),
    });

  // Build Crave Score inputs from current metric card values
  // Dynamic label for sessions based on selected device type
  const sessionsLabel = useMemo(() => {
    switch (deviceType) {
      case "RFID_CENTRAL":
        return t("storeInsights.storePerformance.fields.centralSessions");
      case "IN_AISLE":
        return t("storeInsights.storePerformance.fields.inAisleSessions");
      case "FITTING_ROOM":
        return t("storeInsights.storePerformance.fields.fittingRoomSessions");
      case "MOBILE_PICKUP":
        return t("storeInsights.storePerformance.fields.mobileSessions");
      case "all":
      default:
        return t("storeInsights.storePerformance.fields.sessions");
    }
  }, [deviceType, t]);

  const deviceTypeLabel = useMemo(() => {
    switch (deviceType) {
      case "RFID_CENTRAL":
        return t("storeInsights.storePerformance.labels.central");
      case "IN_AISLE":
        return t("storeInsights.storePerformance.labels.inAisle");
      case "MOBILE_PICKUP":
        return t("storeInsights.storePerformance.labels.mobile");
      case "FITTING_ROOM":
        return t("storeInsights.storePerformance.labels.fittingRoom");
      case "all":
      default:
        return t("storeInsights.storePerformance.labels.allDevices");
    }
  }, [deviceType, t]);

  const craveInputs = useMemo(() => {
    return [
      {
        id: "sessions",
        label: sessionsLabel,
        rawValue: triedOnValueData.rawValue,
      },
      {
        id: "ups",
        label: t("storeInsights.storePerformance.fields.unitsPerSession"),
        rawValue: unitsPerSessionData.rawValue,
      },
      {
        id: "missedPct",
        label: t("storeInsights.storePerformance.fields.missedRequestsPercent"),
        rawValue: missedRequestsData.rawValue,
      },
      {
        id: "avgResponseSec",
        label: t("storeInsights.storePerformance.fields.avgResponseTimeSeconds"),
        rawValue: responseTimeData.rawValue,
      },
      {
        id: "fulfilledPct",
        label: t("storeInsights.storePerformance.fields.fulfilledRequestsPercent"),
        rawValue: fulfilledRequestsData.rawValue,
      },
      {
        id: "cantFindPct",
        label: t("storeInsights.storePerformance.fields.cantFindsPercent"),
        rawValue: cantFindRequestsData.percentage.rawValue,
      },
      {
        id: "canceledPct",
        label: t("storeInsights.storePerformance.fields.cancelledRequestsPercent"),
        rawValue: canceledRequestsData.rawValue,
      },
      {
        id: "totalRequests",
        label: t("storeInsights.storePerformance.fields.totalRequests"),
        rawValue: totalRequestsData.rawValue,
      },
      {
        id: "dwellSec",
        label: t("storeInsights.storePerformance.fields.dwellTimeSeconds"),
        rawValue: dwellTimeData.rawValue,
      },
      {
        id: "namedShoppersPct",
        label: t("storeInsights.storePerformance.fields.namedShoppersPercent"),
        rawValue: namedShoppersData.rawValue,
      },
      {
        id: "satisfaction",
        label: t("storeInsights.storePerformance.fields.experienceRating"),
        rawValue: customerSatisfactionData.rawValue,
      },
    ];
  }, [
    t,
    sessionsLabel,
    triedOnValueData.rawValue,
    unitsPerSessionData.rawValue,
    missedRequestsData.rawValue,
    responseTimeData.rawValue,
    fulfilledRequestsData.rawValue,
    cantFindRequestsData.percentage.rawValue,
    canceledRequestsData.rawValue,
    totalRequestsData.rawValue,
    dwellTimeData.rawValue,
    namedShoppersData.rawValue,
    customerSatisfactionData.rawValue,
  ]);

  const analyticsService = AnalyticsServiceV2.getInstance();

  const [lockSalesInsights, setLockSalesInsights] = useState<boolean>(false);

  const trendCurrentValues = useMemo<Record<StorePerformanceMetric, number | null | undefined>>(
    () => ({
      sessions: triedOnValueData.rawValue,
      unitsPerSession: unitsPerSessionData.rawValue,
      missedRequests: missedRequestsData.rawValue,
      responseTime: responseTimeData.rawValue,
      revenue: revenueCardData.rawValue,
      conversionRate: conversionCardData.rawValue,
      totalRequests: totalRequestsData.rawValue,
      fulfilledRequests: fulfilledRequestsData.rawValue,
      cantFinds: cantFindRequestsData.percentage.rawValue,
      cancelledRequests: canceledRequestsData.rawValue,
      dwellTime: dwellTimeData.rawValue,
      namedShoppers: namedShoppersData.trendRawValue,
      experienceRating: customerSatisfactionData.rawValue,
    }),
    [
      canceledRequestsData.rawValue,
      cantFindRequestsData.percentage.rawValue,
      conversionCardData.rawValue,
      customerSatisfactionData.rawValue,
      dwellTimeData.rawValue,
      fulfilledRequestsData.rawValue,
      missedRequestsData.rawValue,
      namedShoppersData.trendRawValue,
      responseTimeData.rawValue,
      revenueCardData.rawValue,
      totalRequestsData.rawValue,
      triedOnValueData.rawValue,
      unitsPerSessionData.rawValue,
    ],
  );
  const { comparisonRange, salesComparisonWindow, trends, comparisonProgress } =
    useStorePerformanceTrends({
      enabled: Boolean(
        selectedStore &&
        !loading &&
        !missedRequestsData.loading &&
        !cantFindRequestsData.percentage.loading &&
        !canceledRequestsData.loading,
      ),
      storeIds: selectedStore ? [selectedStore.id] : [],
      comparisonWindow,
      anchorComparisonToWeekdays:
        (period === "last_4_weeks" || period === "custom") &&
        (comparisonWindow === "month_before" || comparisonWindow === "year_before")
          ? anchorComparisonToWeekdays
          : true,
      currentRange: currentDateRange,
      deviceType,
      areaIds,
      latestSalesDate,
      metrics:
        supportsStoreRevenue && supportsConversionRate
          ? undefined
          : storePerformanceTrendMetrics.filter(
              (metric) =>
                (supportsStoreRevenue || metric !== "revenue") &&
                (supportsConversionRate || metric !== "conversionRate"),
            ),
      currentValues: trendCurrentValues,
    });
  const formatPreviousTrendValue = useCallback(
    (metric: StorePerformanceMetric, value: number, locale: string) => {
      switch (metric) {
        case "sessions":
          return formatInteger(value, locale);
        case "unitsPerSession":
          return formatDecimal(value, locale, { maximumFractionDigits: 1 });
        case "missedRequests":
          return formatPercentFromWhole(value, locale);
        case "responseTime":
          return formatDurationShort(value, locale);
        case "revenue":
          return formatCurrencyInStore(value, selectedStore, new Intl.Locale(locale));
        case "conversionRate":
          return formatPercentFromWhole(value, locale);
        case "totalRequests":
          return formatInteger(value, locale);
        case "fulfilledRequests":
          return formatPercentFromWhole(value, locale);
        case "cantFinds":
          return formatPercentFromWhole(value, locale);
        case "cancelledRequests":
          return formatPercentFromWhole(value, locale);
        case "dwellTime":
          return formatDurationShort(value, locale);
        case "namedShoppers":
          return formatPercentFromWhole(value, locale);
        case "experienceRating":
          return formatDecimal(value, locale, { maximumFractionDigits: 1 });
      }
    },
    [selectedStore],
  );
  const trendProps = useCallback(
    (metric: StorePerformanceMetric) => {
      if (!showTrends) return undefined;

      const trend = trends[metric];
      const locale = i18n.resolvedLanguage || i18n.language;
      const isSalesMetric = metric === "revenue" || metric === "conversionRate";
      const range = isSalesMetric
        ? formatStoreDateRange(
            parseStoreDateKey(salesComparisonWindow.startDate, browserTimezone) ??
              salesComparisonWindow.startDate,
            parseStoreDateKey(salesComparisonWindow.endDate, browserTimezone) ??
              salesComparisonWindow.endDate,
            browserTimezone,
            locale,
            { weekday: "short", month: "short", day: "numeric" },
          )
        : formatStoreDateRange(
            comparisonRange.start,
            comparisonRange.end,
            browserTimezone,
            locale,
            {
              weekday: "short",
              month: "short",
              day: "numeric",
            },
          );
      const dateRange =
        range.startDate === range.endDate
          ? range.startDate
          : `${range.startDate} – ${range.endDate}`;
      const comparisonProgressText = comparisonProgress[metric]
        ? metric === "namedShoppers"
          ? t("common.analytics.namedShoppersOfTotalSessions", {
              namedCount: comparisonProgress[metric].current,
              totalCount: comparisonProgress[metric].total,
            })
          : t("common.analytics.requestsProgress", comparisonProgress[metric])
        : undefined;
      return {
        ...trend,
        text: formatTrendChange(
          trend,
          (value) => formatPreviousTrendValue(metric, value, locale),
          (value) => formatPercentFromWhole(value, locale),
          t("common.analytics.trends.noChange"),
        ),
        tooltip:
          trend.status === "ready"
            ? t("common.analytics.trends.tooltip", {
                dateRange,
                previousValue: [
                  formatPreviousTrendValue(metric, trend.previousValue, locale),
                  comparisonProgressText,
                ]
                  .filter(Boolean)
                  .join(" • "),
              })
            : undefined,
      };
    },
    [
      browserTimezone,
      comparisonRange.end,
      comparisonRange.start,
      formatPreviousTrendValue,
      salesComparisonWindow.endDate,
      salesComparisonWindow.startDate,
      showTrends,
      t,
      trends,
      comparisonProgress,
    ],
  );
  const salesWindowWarning = (salesWindow: typeof revenueCardData.salesWindow) => {
    if (!salesWindow?.isLimited) return undefined;

    const locale = i18n.resolvedLanguage || i18n.language;
    const range = formatStoreDateRange(
      parseStoreDateKey(salesWindow.startDate, browserTimezone) ?? salesWindow.startDate,
      parseStoreDateKey(salesWindow.endDate, browserTimezone) ?? salesWindow.endDate,
      browserTimezone,
      locale,
      { month: "short", day: "numeric", year: "numeric" },
    );
    return (
      <Tooltip
        title={t("common.analytics.salesDataLimitedToWindow", {
          latestSalesDate: range.endDate,
        })}
      >
        <EventBusyOutlined sx={{ color: "common.black", fontSize: "1em" }} />
      </Tooltip>
    );
  };

  useEffect(() => {
    let isCurrent = true;

    const checkLatestSalesFreshness = async () => {
      try {
        if (!selectedStore?.id) {
          if (!isCurrent) return;
          setLockSalesInsights(false);
          setLatestSalesDate(null);
          setLatestFootTrafficDate(null);
          setSalesWindowStoreId(null);
          return;
        }

        const latest = await analyticsService.getLatestSalesDateForStore(selectedStore.id);
        if (!isCurrent) return;
        console.log("Latest sales date for store", selectedStore.id, latest);
        setLatestSalesDate(latest);
        setSalesWindowStoreId(selectedStore.id);
        // Also get latest fitting room traffic date
        // const latestTraffic = await analyticsService.getLatestFootTrafficDateForStore(selectedStore.id);
        // setLatestFootTrafficDate(latestTraffic);
        if (!latest) {
          setLockSalesInsights(true);
          return;
        }

        const now = new Date();
        const diffMs = now.getTime() - latest.getTime();
        const sixtyDaysMs = 60 * 24 * 60 * 60 * 1000; // 60 days
        setLockSalesInsights(diffMs > sixtyDaysMs);
      } catch (e) {
        if (!isCurrent) return;
        console.error("Failed to check latest sales date", e);
        setLockSalesInsights(false);
        setSalesWindowStoreId(selectedStore?.id ?? null);
      }
    };

    checkLatestSalesFreshness();

    return () => {
      isCurrent = false;
    };
  }, [selectedStore?.id]);

  // Helper: safely update search params without dropping existing keys
  // Format YYYY-MM-DD for URL
  const fmtYMD = (d: Date) => storePerformanceService.formatDate(d, browserTimezone);

  const handleNavigateToReport = (reportType: "sessions" | "missed" | "cant-find") => {
    const range = currentDateRange;
    const params = new URLSearchParams();
    if (period === "custom") {
      params.set("period", "custom");
      params.set("start", fmtYMD(range.start));
      params.set("end", fmtYMD(range.end));
    } else {
      params.set("period", period);
    }
    if (selectedStore) params.set("storeIds", String(selectedStore.id));
    if (deviceType && deviceType !== "all") params.set("deviceType", deviceType);
    navigate(`/reports/${reportType}?${params.toString()}`);
  };

  // Keyboard shortcuts: '}' or Shift+ArrowDown for next store, '{' or Shift+ArrowUp for previous store
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Avoid interfering while typing in inputs/textareas or contenteditable
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName?.toLowerCase();
        if (tag === "input" || tag === "textarea" || target.isContentEditable) {
          return;
        }
      }

      if (!stores.length) return;

      // Determine intended action
      let action: "next" | "prev" | null = null;
      if (e.key === "}" || (e.shiftKey && e.key === "ArrowDown")) action = "next";
      else if (e.key === "{" || (e.shiftKey && e.key === "ArrowUp")) action = "prev";
      if (!action) return;

      // Lightweight throttle to prevent ultra-fast repeats while responding instantly
      const now = Date.now();
      if (now - lastShortcutAtRef.current < 150) {
        e.preventDefault();
        return;
      }
      lastShortcutAtRef.current = now;

      // Respect demo restrictions consistent with selection rules
      const isDemoStore = (s: Store) => {
        const name = String((s as any).name || "").toLowerCase();
        const group = String((s as any).group || "").toLowerCase();
        return name.includes("demo") || group.includes("demo");
      };
      const demoRestricted = !(user?.role && user.role === UserRole.CRAVE_ADMIN);
      const visibleStores = demoRestricted ? stores.filter((s) => !isDemoStore(s)) : stores;

      const currentIndex = selectedStore
        ? visibleStores.findIndex((s) => s.id === selectedStore.id)
        : -1;
      if (action === "next") {
        if (currentIndex >= 0 && currentIndex < visibleStores.length - 1) {
          const next = visibleStores[currentIndex + 1];
          onStoreChange(next);
        }
      } else if (action === "prev") {
        if (currentIndex > 0) {
          const prev = visibleStores[currentIndex - 1];
          onStoreChange(prev);
        }
      }

      e.preventDefault();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (shortcutDebounceRef.current) {
        clearTimeout(shortcutDebounceRef.current);
      }
    };
  }, [stores, selectedStore, onStoreChange, user?.role]);

  return (
    <div className="page-scroll-stable store-performance-page">
      <ComparisonPageHeaderShell
        title={t("storeInsights.storePerformance.title")}
        gradientTitle
        icon={<StoreMallDirectory />}
        loading={titleLoading}
        headerControls={
          <InsightsHeaderSelectorRow className="store-performance-print-hide">
            <InsightsHeaderSelectorField sx={{ width: { xs: "100%", md: 340, lg: 440 } }}>
              <StoreMultiSelect
                className="w-full font-bold"
                minDropdownWidth={440}
                dropdownAlign="right"
                options={stores}
                value={selectedStore ? [selectedStore] : []}
                currentUserRole={user?.role}
                isDemo={(s) => {
                  const name = String((s as any).name || "").toLowerCase();
                  const group = String((s as any).group || "").toLowerCase();
                  return name.includes("demo") || group.includes("demo");
                }}
                onChange={(next) => {
                  const sel = next && next.length > 0 ? next[0] : null;
                  onStoreChange(sel);
                }}
                label={t("storeInsights.storePerformance.fields.store")}
                disabled={loading || stores.length === 0}
                loading={loading}
                getOptionLabel={(s) => formatStoreSelectorLabel(s, t, user?.role, "id-name")}
                getOptionKey={(s) => (s as any).id as number}
                emptyText={t("storeInsights.storePerformance.empty.noStores")}
                single
              />
            </InsightsHeaderSelectorField>
          </InsightsHeaderSelectorRow>
        }
        secondaryControls={
          <div className="flex flex-col gap-4 mb-4">
            <div className="w-full store-performance-print-hide">
              <ReportingWindowSelector
                className="store-performance-date-filter"
                value={period}
                onChange={onPeriodChange}
                showRangeText
                dateRange={currentDateRange}
                customRange={customRange}
                onCustomRangeChange={onCustomRangeChange}
                showDeviceFilter
                areaFilter={
                  <AreaFilter
                    areas={areas}
                    loading={areasLoading}
                    disabled={loading || !selectedStore}
                    selectedAreaIds={areaIds ?? []}
                    onAreaIdsChange={(nextAreaIds) =>
                      setAreaSelection(
                        nextAreaIds.length === 0 || !selectedStore
                          ? null
                          : { storeId: selectedStore.id, areaIds: nextAreaIds },
                      )
                    }
                  />
                }
                showTrendSelector
                trendPeriod={comparisonWindow}
                onTrendPeriodChange={setComparisonWindow}
                trendComparisonRange={comparisonRange}
                anchorTrendComparisonToWeekdays={anchorComparisonToWeekdays}
                onAnchorTrendComparisonToWeekdaysChange={setAnchorComparisonToWeekdays}
                selectedDeviceType={deviceType}
                onDeviceTypeChange={onDeviceTypeChange}
              />
            </div>
            <div className="hidden w-full print:block">
              <ReportingWindowSelector
                className="store-performance-date-filter"
                value={period}
                onChange={onPeriodChange}
                showRangeText
                dateRange={currentDateRange}
                customRange={customRange}
                onCustomRangeChange={onCustomRangeChange}
                trendPeriod={comparisonWindow}
                trendComparisonRange={comparisonRange}
              />
            </div>
          </div>
        }
        printMeta={
          <div className="store-performance-print-only store-performance-print-meta">
            {selectedStore && <span>{selectedStore.name}</span>}
            {selectedAreaNames.length > 0 && (
              <span>
                {new Intl.ListFormat(i18n.resolvedLanguage || i18n.language).format(
                  selectedAreaNames,
                )}
              </span>
            )}
            <span>{deviceTypeLabel}</span>
          </div>
        }
      />
      <Box sx={{ p: { xs: 2, md: 2.5 }, pt: 0 }}>
        {/* Crave Score section above Key Performance Levers */}
        <Box sx={{ mt: 3 }}>
          <CraveScoreCard inputs={craveInputs} config={getCraveScoreConfigForDevice(deviceType)} />
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography
            component="h2"
            variant="h6"
            sx={{ mb: 2, fontSize: 20, fontWeight: 600, textAlign: "start" }}
          >
            {t("storeInsights.storePerformance.sections.keyPerformanceLevers")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                lg: "repeat(2, minmax(0, 1fr))",
                xl: supportsRequestInsights
                  ? "repeat(4, minmax(0, 1fr))"
                  : "repeat(2, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <div className="min-w-0">
              <AnalyticsCard
                value={triedOnValueData.value}
                previousText={triedOnValueData.previousValue}
                title={sessionsLabel}
                isPositive={triedOnValueData.isPositive}
                subtitle={triedOnValueData.subtitle}
                icon={<LocalAtm />}
                pillText={triedOnValueData.pillText}
                trend={trendProps("sessions")}
                subtitleLoading={trends.sessions.status === "loading"}
                onClick={() => handleNavigateToReport("sessions")}
                countUp
                disableFade
              />
            </div>

            <div className="min-w-0">
              <AnalyticsCard
                value={unitsPerSessionData.value}
                previousText={unitsPerSessionData.previousValue}
                title={t("storeInsights.storePerformance.fields.unitsPerSession")}
                isPositive={unitsPerSessionData.isPositive}
                subtitle={unitsPerSessionData.subtitle}
                icon={<Checkroom />}
                pillText={unitsPerSessionData.pillText}
                trend={trendProps("unitsPerSession")}
                subtitleLoading={trends.unitsPerSession.status === "loading"}
              />
            </div>

            {supportsRequestInsights && (
              <>
                <div className="min-w-0">
                  <AnalyticsCard
                    value={missedRequestsData.value}
                    previousText={missedRequestsData.previousValue}
                    title={t("storeInsights.storePerformance.fields.missedRequests")}
                    isPositive={missedRequestsData.isPositive}
                    subtitle={missedRequestsData.subtitle}
                    icon={<CrisisAlert />}
                    pillText={missedRequestsData.pillText}
                    trend={trendProps("missedRequests")}
                    subtitleLoading={trends.missedRequests.status === "loading"}
                    onClick={() => handleNavigateToReport("missed")}
                  />
                </div>

                <div className="min-w-0">
                  <AnalyticsCard
                    value={responseTimeData.value}
                    previousText={responseTimeData.previousValue}
                    title={t("storeInsights.storePerformance.fields.avgResponseTime")}
                    isPositive={responseTimeData.isPositive}
                    subtitle={responseTimeData.subtitle}
                    icon={<AccessTime />}
                    pillText={responseTimeData.pillText}
                    trend={trendProps("responseTime")}
                  />
                </div>
              </>
            )}

            {/*
            <div className="flex-1 min-w-[300px]">
              <AnalyticsCard
                value={footTrafficCardData.value}
                previousText={footTrafficCardData.previousValue}
                title="Traffic to fitting room"
                isPositive={footTrafficCardData.isPositive}
                subtitle={footTrafficCardData.subtitle}
                icon={<Groups/>}
                pillText={footTrafficCardData.pillText}
              />
            </div>
            */}

            {/*
            <div className="flex-1 min-w-[300px]">
              <AnalyticsCard
                value={digitalEngagementData.value}
                previousText={digitalEngagementData.previousValue}
                title="Digital Engagement"
                isPositive={digitalEngagementData.isPositive}
                subtitle={digitalEngagementData.subtitle}
                icon={<TrendingUp/>}
                pillText={digitalEngagementData.pillText}
                showGradient={false}
              />
            </div>
            */}
          </Box>
        </Box>

        {(supportsStoreRevenue || supportsConversionRate) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
            {supportsStoreRevenue && (
              <div className="flex-1 min-w-[300px]">
                {lockSalesInsights ? (
                  <LockedAnalyticsCard
                    title={t("storeInsights.storePerformance.fields.storeRevenue")}
                    icon={<LocalAtm />}
                  />
                ) : (
                  <AnalyticsCard
                    value={revenueCardData.value}
                    previousText={revenueCardData.previousValue}
                    title={t("storeInsights.storePerformance.fields.storeRevenue")}
                    isPositive={revenueCardData.isPositive}
                    subtitle={revenueCardData.subtitle}
                    icon={<LocalAtm />}
                    pillText={revenueCardData.pillText}
                    trend={trendProps("revenue")}
                    showGradient
                    bottomAdornment={salesWindowWarning(revenueCardData.salesWindow)}
                  />
                )}
              </div>
            )}
            {supportsConversionRate && (
              <div className="flex-1 min-w-[300px]">
                {lockSalesInsights ? (
                  <LockedAnalyticsCard
                    title={t("storeInsights.storePerformance.fields.conversionRate")}
                    icon={<TrendingUp />}
                  />
                ) : (
                  <AnalyticsCard
                    value={conversionCardData.value}
                    previousText={conversionCardData.previousValue}
                    title={t("storeInsights.storePerformance.fields.conversionRate")}
                    isPositive={conversionCardData.isPositive}
                    subtitle={conversionCardData.subtitle}
                    icon={<TrendingUp />}
                    pillText={conversionCardData.pillText}
                    trend={trendProps("conversionRate")}
                    showGradient
                    bottomAdornment={salesWindowWarning(conversionCardData.salesWindow)}
                  />
                )}
              </div>
            )}
          </Box>
        )}

        {supportsRequestInsights && (
          <Box sx={{ mt: 4 }}>
            <Typography
              component="h2"
              variant="h6"
              sx={{ mb: 2, fontSize: 20, fontWeight: 600, textAlign: "start" }}
            >
              {t("storeInsights.storePerformance.sections.requestsBreakdown")}
            </Typography>
            {/* Row 1: Total Requests and Fulfilled (Avg Response Time card commented out per requirement) */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
              <div className="flex-1 min-w-[300px]">
                <AnalyticsCard
                  value={totalRequestsData.value}
                  previousText={totalRequestsData.previousValue}
                  title={t("storeInsights.storePerformance.fields.totalRequests")}
                  isPositive={totalRequestsData.isPositive}
                  subtitle={totalRequestsData.subtitle}
                  icon={<ListAlt />}
                  pillText={null}
                  trend={trendProps("totalRequests")}
                  subtitleLoading={trends.totalRequests.status === "loading"}
                  showGradient={true}
                />
              </div>

              {/*
            <div className="flex-1 min-w-[300px]">
              <AnalyticsCard
                value={responseTimeData.value}
                previousText={responseTimeData.previousValue}
                title="Avg Response Time"
                isPositive={responseTimeData.isPositive}
                subtitle={responseTimeData.subtitle}
                icon={<AccessTime/>}
                pillText={responseTimeData.pillText}
                showGradient={true}
              />
            </div>
            */}

              <div className="flex-1 min-w-[300px]">
                <AnalyticsCard
                  value={fulfilledRequestsData.value}
                  previousText={fulfilledRequestsData.previousValue}
                  title={t("storeInsights.storePerformance.fields.fulfilledRequests")}
                  isPositive={fulfilledRequestsData.isPositive}
                  subtitle={fulfilledRequestsData.subtitle}
                  icon={<CheckCircle />}
                  pillText={fulfilledRequestsData.pillText}
                  trend={trendProps("fulfilledRequests")}
                  subtitleLoading={trends.fulfilledRequests.status === "loading"}
                  showGradient={true}
                />
              </div>
            </Box>

            {/* Row 2: Remaining request metrics */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <div className="flex-1 min-w-[300px]">
                <AnalyticsCard
                  value={missedRequestsData.value}
                  previousText={missedRequestsData.previousValue}
                  title={t("storeInsights.storePerformance.fields.missedRequests")}
                  isPositive={missedRequestsData.isPositive}
                  subtitle={missedRequestsData.subtitle}
                  icon={<CrisisAlert />}
                  pillText={missedRequestsData.pillText}
                  trend={trendProps("missedRequests")}
                  subtitleLoading={trends.missedRequests.status === "loading"}
                  showGradient={true}
                  onClick={() => handleNavigateToReport("missed")}
                />
              </div>

              <div className="flex-1 min-w-[300px]">
                <AnalyticsCard
                  value={cantFindRequestsData.percentage.value}
                  previousText={cantFindRequestsData.percentage.previousValue}
                  title={t("storeInsights.storePerformance.fields.cantFinds")}
                  isPositive={cantFindRequestsData.percentage.isPositive}
                  subtitle={cantFindRequestsData.percentage.subtitle}
                  icon={<Warning />}
                  pillText={cantFindRequestsData.percentage.pillText}
                  trend={trendProps("cantFinds")}
                  subtitleLoading={trends.cantFinds.status === "loading"}
                  showGradient={true}
                  onClick={() => handleNavigateToReport("cant-find")}
                />
              </div>

              <div className="flex-1 min-w-[300px]">
                <AnalyticsCard
                  value={canceledRequestsData.value}
                  previousText={canceledRequestsData.previousValue}
                  title={t("storeInsights.storePerformance.fields.cancelledRequests")}
                  isPositive={canceledRequestsData.isPositive}
                  subtitle={canceledRequestsData.subtitle}
                  icon={<Cancel />}
                  pillText={canceledRequestsData.pillText}
                  trend={trendProps("cancelledRequests")}
                  subtitleLoading={trends.cancelledRequests.status === "loading"}
                  showGradient={true}
                />
              </div>
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 4 }}>
          <Typography
            component="h2"
            variant="h6"
            sx={{ mb: 2, fontSize: 20, fontWeight: 600, textAlign: "start" }}
          >
            {t("storeInsights.storePerformance.sections.sessionBreakdown")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <div className="flex-1 min-w-[300px]">
              <AnalyticsCard
                value={dwellTimeData.value}
                previousText={dwellTimeData.previousValue}
                title={t("storeInsights.storePerformance.fields.dwellTime")}
                isPositive={dwellTimeData.isPositive}
                subtitle={dwellTimeData.subtitle}
                icon={<AccessTime />}
                pillText={dwellTimeData.pillText}
                trend={trendProps("dwellTime")}
                showGradient={true}
              />
            </div>

            <div className="flex-1 min-w-[300px]">
              <AnalyticsCard
                value={namedShoppersData.value}
                previousText={namedShoppersData.previousValue}
                title={t("storeInsights.storePerformance.fields.namedShoppers")}
                isPositive={namedShoppersData.isPositive}
                subtitle={namedShoppersData.subtitle}
                icon={<Groups />}
                pillText={namedShoppersData.pillText}
                pillTextAsChip
                trend={trendProps("namedShoppers")}
                subtitleLoading={trends.namedShoppers.status === "loading"}
                showGradient={true}
              />
            </div>

            <div className="flex-1 min-w-[300px]">
              <AnalyticsCard
                value={renderStarRating(customerSatisfactionData.value)}
                previousText={customerSatisfactionData.previousValue}
                title={t("storeInsights.storePerformance.fields.experienceRating")}
                isPositive={customerSatisfactionData.isPositive}
                subtitle={customerSatisfactionData.subtitle}
                icon={<ChatBubble />}
                pillText={customerSatisfactionData.pillText}
                trend={trendProps("experienceRating")}
                subtitleLoading={trends.experienceRating.status === "loading"}
                showGradient={true}
              />
            </div>
          </Box>
        </Box>

        {period !== "today" && (
          <Box sx={{ mt: 4, "& > div": { mt: 0 } }}>
            <StoreInsightsPanel
              title={t("storeInsights.storePerformance.sections.mostTriedOnItems")}
            >
              {popularItemsLoading ? (
                <Box sx={{ mb: 2.5 }}>
                  <LongRunningDataLoader
                    variant="table"
                    isLoading
                    baseLabels={[
                      t("storeInsights.storePerformance.loading.popularItems"),
                      t("storeInsights.storePerformance.loading.popularItemsFinding"),
                      t("storeInsights.storePerformance.loading.popularItemsDetails"),
                      t("storeInsights.storePerformance.loading.popularItemsMatching"),
                      t("storeInsights.storePerformance.loading.popularItemsFinalizing"),
                    ]}
                    loopingLabels={[
                      t("storeInsights.storePerformance.loading.popularItemsPreparing"),
                      t("storeInsights.storePerformance.loading.popularItemsWaiting"),
                    ]}
                    expectedMeanTimeMs={5_000}
                    locale={i18n.resolvedLanguage || i18n.language}
                    estimateLabel={(duration) =>
                      t("storeComparison.loading.usuallyUnder", { duration })
                    }
                    loadingKey={popularItemsLoadingKey}
                  />
                </Box>
              ) : null}
              {isMobile ? (
                <>
                  <RecordSortControl
                    value={popularItemsSortField}
                    direction={popularItemsSortDirection}
                    options={[
                      {
                        value: "title" as const,
                        label: t("storeInsights.cantFindOccurrencesTable.columns.product"),
                      },
                      {
                        value: "sku" as const,
                        label: t("storeInsights.cantFindOccurrencesTable.columns.sku"),
                      },
                      {
                        value: "size" as const,
                        label: t("storeInsights.cantFindOccurrencesTable.columns.size"),
                      },
                      {
                        value: "triedOnTimes" as const,
                        label: t("storeInsights.popularProductsReport.fields.triedOn"),
                      },
                      {
                        value: "numberSold" as const,
                        label: t("storeInsights.popularProductsReport.cards.unitsSold"),
                      },
                      { value: "quantityInStock" as const, label: t("inventory.inStock") },
                    ]}
                    onSort={handlePopularItemsSort}
                  />
                  <RecordList
                    items={sortedPopularItems}
                    getItemId={(item) => item.sku}
                    loading={popularItemsLoading}
                    resetKey={`${popularItemsLoadingKey}:${popularItemsSortField}:${popularItemsSortDirection}`}
                    renderTitle={(item) => item.title}
                    renderThumbnail={(item) => (
                      <PlaceholderImage
                        alt={item.title}
                        className="h-10 w-10 rounded"
                        imageUrl={item.imageUrl}
                      />
                    )}
                    renderSummary={(item) => (
                      <RecordFields
                        fields={[
                          {
                            label: t("storeInsights.popularProductsReport.fields.triedOn"),
                            value: formatInteger(item.triedOnTimes, translationI18n.language),
                          },
                          {
                            label: t("storeInsights.popularProductsReport.cards.unitsSold"),
                            value: formatInteger(item.numberSold, translationI18n.language),
                          },
                        ]}
                      />
                    )}
                    renderDetails={(item) => (
                      <RecordFields
                        fields={[
                          {
                            label: t("storeInsights.cantFindOccurrencesTable.columns.sku"),
                            value: item.sku,
                          },
                          {
                            label: t("storeInsights.cantFindOccurrencesTable.columns.size"),
                            value: item.size || "–",
                          },
                          {
                            label: t("inventory.inStock"),
                            value: formatInteger(item.quantityInStock, translationI18n.language),
                          },
                        ]}
                      />
                    )}
                    errorState={
                      popularItemsData.error ? (
                        <Typography role="alert" color="error">
                          {t("storeInsights.storePerformance.errors.popularItems", {
                            error: popularItemsData.error,
                          })}
                        </Typography>
                      ) : undefined
                    }
                    emptyState={
                      <AdminEmptyState
                        icon={<SearchOffOutlined fontSize="large" />}
                        text={t("storeInsights.storePerformance.empty.noPopularItems")}
                      />
                    }
                  />
                </>
              ) : (
                <TableContainer>
                  <Table sx={tableInCardSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {t("storeInsights.cantFindOccurrencesTable.columns.image")}
                        </TableCell>
                        <PopularItemsSortableHeader
                          field="title"
                          label={t("storeInsights.cantFindOccurrencesTable.columns.product")}
                          nextSortDirectionLabel={popularItemsNextSortLabel("title")}
                          onSort={handlePopularItemsSort}
                          sortDirection={popularItemsSortDirection}
                          sortField={popularItemsSortField}
                        />
                        <PopularItemsSortableHeader
                          field="sku"
                          label={t("storeInsights.cantFindOccurrencesTable.columns.sku")}
                          nextSortDirectionLabel={popularItemsNextSortLabel("sku")}
                          onSort={handlePopularItemsSort}
                          sortDirection={popularItemsSortDirection}
                          sortField={popularItemsSortField}
                        />
                        <PopularItemsSortableHeader
                          field="size"
                          label={t("storeInsights.cantFindOccurrencesTable.columns.size")}
                          nextSortDirectionLabel={popularItemsNextSortLabel("size")}
                          onSort={handlePopularItemsSort}
                          sortDirection={popularItemsSortDirection}
                          sortField={popularItemsSortField}
                        />
                        <PopularItemsSortableHeader
                          align="right"
                          field="triedOnTimes"
                          label={t("storeInsights.popularProductsReport.fields.triedOn")}
                          nextSortDirectionLabel={popularItemsNextSortLabel("triedOnTimes")}
                          onSort={handlePopularItemsSort}
                          sortDirection={popularItemsSortDirection}
                          sortField={popularItemsSortField}
                        />
                        <PopularItemsSortableHeader
                          align="right"
                          field="numberSold"
                          label={t("storeInsights.popularProductsReport.cards.unitsSold")}
                          nextSortDirectionLabel={popularItemsNextSortLabel("numberSold")}
                          onSort={handlePopularItemsSort}
                          sortDirection={popularItemsSortDirection}
                          sortField={popularItemsSortField}
                        />
                        <PopularItemsSortableHeader
                          align="right"
                          field="quantityInStock"
                          label={t("inventory.inStock")}
                          nextSortDirectionLabel={popularItemsNextSortLabel("quantityInStock")}
                          onSort={handlePopularItemsSort}
                          sortDirection={popularItemsSortDirection}
                          sortField={popularItemsSortField}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {popularItemsLoading ? (
                        <TableSkeletonRows
                          columnCount={7}
                          alignments={["left", "left", "left", "left", "right", "right", "right"]}
                          imageColumns={[0]}
                          imageSize={40}
                        />
                      ) : popularItemsData.error ? (
                        <TableRow className="table-empty-state">
                          <TableCell align="center" colSpan={7}>
                            <Typography color="error">
                              {t("storeInsights.storePerformance.errors.popularItems", {
                                error: popularItemsData.error,
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : sortedPopularItems.length === 0 ? (
                        <TableRow className="table-empty-state">
                          <TableCell colSpan={7}>
                            <AdminEmptyState
                              icon={<SearchOffOutlined aria-hidden="true" fontSize="large" />}
                              text={t("storeInsights.storePerformance.empty.noPopularItems")}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        sortedPopularItems.map((item) => (
                          <TableRow key={item.sku}>
                            <TableCell>
                              <PlaceholderImage
                                alt={item.title}
                                className="h-10 w-10 rounded"
                                imageUrl={item.imageUrl}
                              />
                            </TableCell>
                            <TableCell>{item.title}</TableCell>
                            <TableCell>{item.sku}</TableCell>
                            <TableCell>{item.size || "–"}</TableCell>
                            <TableCell align="right">
                              {formatInteger(
                                item.triedOnTimes,
                                i18n.resolvedLanguage || i18n.language,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {formatInteger(
                                item.numberSold,
                                i18n.resolvedLanguage || i18n.language,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {formatInteger(
                                item.quantityInStock,
                                i18n.resolvedLanguage || i18n.language,
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </StoreInsightsPanel>
          </Box>
        )}
      </Box>
      <DataFreshnessFooter
        icon={freshnessFooterIcon}
        text={freshnessFooterText}
        tooltip={freshnessFooterTooltip}
      />
    </div>
  );
};

export default StorePerformance;
